import { getApiUrl } from "@dashboard/config";
import { PermissionEnum, useAppCreateMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router";

import { AppDetailPage } from "../components/AppDetailPage";
import {
  getServiceConfigurations,
  postConfigurationUpdate,
  updateChangedServiceConfigurations,
} from "../services/configurationApi";
import { ProcessedService, ThirdPartyServicesFormData } from "../types";
import CheckoutQuestionsView from "./CheckoutQuestionsView";
import FulfillmentWindowView from "./FulfillmentWindowView";
import ShippingEstimatorView from "./ShippingEstimatorView";
import ShippingRestrictionsView from "./ShippingRestrictionsView";
import TieredPricingView from "./TieredPricingView";

interface AppDetailParams {
  appType: string;
}

const AppDetailView: React.FC = () => {
  const { appType } = useParams<AppDetailParams>();
  const intl = useIntl();
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [initialData, setInitialData] = useState<ProcessedService[]>([]);
  const [createApp] = useAppCreateMutation();

  // Function to create dealer_locator app via GraphQL when user enables it
  const createDealerLocatorApp = async (): Promise<boolean> => {
    try {
      const result = await createApp({
        variables: {
          input: {
            name: "Dealers",
            permissions: [
              PermissionEnum.MANAGE_CHANNELS,
              PermissionEnum.MANAGE_PRODUCTS,
              PermissionEnum.MANAGE_SETTINGS,
              PermissionEnum.MANAGE_SHIPPING,
            ],
          },
        },
      });

      if (result.data?.appCreate?.errors?.length === 0 && result.data?.appCreate?.authToken) {
        const authToken = result.data.appCreate.authToken;
        const TENANT_URL = getApiUrl();

        if (!TENANT_URL) {
          throw new Error("API_URL environment variable is not configured");
        }

        // Call POST endpoint to save configuration
        const response = await postConfigurationUpdate({
          tenant: TENANT_URL,
          app_name: "dealer_locator",
          configurations: {
            token: authToken,
          },
          is_active: true,
        });

        if (!response.ok) {
          throw new Error(`Failed to save dealer_locator configuration: ${response.status}`);
        }

        console.warn("Dealer locator app created and configured successfully");

        return true;
      } else {
        console.error("Failed to create dealer locator app:", result.data?.appCreate?.errors);

        return false;
      }
    } catch (err) {
      console.error("Error creating dealer locator app:", err);

      return false;
    }
  };

  useEffect(() => {
    const loadConfigurationsFromAPI = async () => {
      try {
        setInitialLoading(true);

        const services = await getServiceConfigurations();

        setInitialData(services);
      } catch (error) {
        console.error("Failed to load configurations from API:", error);
        setErrors([`Failed to load configurations: ${(error as Error).message}`]);
        setInitialData([]);
      } finally {
        setInitialLoading(false);
      }
    };

    loadConfigurationsFromAPI();
  }, []);

  // Handle special views separately
  if (appType === "checkout-question") {
    return <CheckoutQuestionsView />;
  }

  if (appType === "shipping-estimator") {
    return <ShippingEstimatorView />;
  }

  if (appType === "fulfillment-window") {
    return <FulfillmentWindowView />;
  }

  if (appType === "shipping-restrictions") {
    return <ShippingRestrictionsView />;
  }

  if (appType === "tiered-pricing") {
    return <TieredPricingView />;
  }

  const handleSubmit = async (data: ThirdPartyServicesFormData) => {
    setLoading(true);
    setErrors([]);

    try {
      // Only update services that have actually changed, with dealer_locator creation support
      await updateChangedServiceConfigurations(initialData, data.services, createDealerLocatorApp);

      // Show success toast
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });

      // Reload data to reflect the saved changes
      try {
        const updatedServices = await getServiceConfigurations();

        setInitialData(updatedServices);
      } catch (reloadError) {
        console.warn("Failed to reload data after save:", reloadError);
      }
    } catch (error) {
      console.error(`Failed to save ${appType}:`, error);
      setErrors([`Failed to save configurations: ${(error as Error).message}`]);
      notify({
        status: "error",
        text: intl.formatMessage(commonMessages.somethingWentWrong),
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AppDetailPage
      disabled={loading}
      errors={errors}
      saveButtonBarState={loading ? "loading" : "default"}
      onSubmit={handleSubmit}
      initialData={initialData}
      appType={appType || ""}
    />
  );
};

export default AppDetailView;
